import React from 'react';
import { ArrowRight, Droplets, Maximize, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import img1 from "../../assets/bathroom1.jpg";
import img2 from "../../assets/bathroom2.jpg";
import img3 from "../../assets/bathroom3.jpg";
import img4 from "../../assets/bathroom4.jpg";
import img5 from "../../assets/bathroom5.jpg";

// Bathroom Data
const designs = [
  { 
    id: 1, 
    title: 'Modern Marble Suite', 
    category: 'Modern', 
    image: img1 
  },
  { 
    id: 2, 
    title: 'Warm Spa Retreat', 
    category: 'Zen', 
    image: img2 
  },
  { 
    id: 3, 
    title: 'Black & Gold Luxury', 
    category: 'Industrial', 
    image: img3
  },
  { 
    id: 4, 
    title: 'Classic White Vanity', 
    category: 'Classic', 
    image: img4
  },
];

const Bathroom = () => {
  return (
    <div className="font-sans text-gray-800 bg-white">
      
      {/* 1. Hero Section - Targeted for Relaxation */}
      <div
        className="relative h-[60vh] w-full bg-cover bg-center"
        style={{ backgroundImage: `url(${img5})` }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4 animate-fadeIn">
          <h4 className="text-amber-500 font-bold tracking-widest uppercase mb-3">
             Personal Sanctuary
          </h4>
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mb-4">
            Luxury Bathrooms
          </h1>
          <p className="text-gray-200 text-lg max-w-xl font-light">
            Designing spa-like retreats in <strong>Delhi, Noida & Varanasi</strong>.
          </p>
        </div>
      </div>

      {/* 2. Intro Section - Trust Building */}
      <section className="py-16 px-6 bg-white text-center">
        <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-4">
          Refresh & Rejuvenate
        </h2>
        <div className="w-20 h-1 bg-amber-500 mx-auto mb-6"></div>
        <p className="text-gray-600 max-w-3xl mx-auto leading-relaxed text-lg">
          Your bathroom should be more than just functional. We design spaces that feel like a personal spa, 
          using high-end <strong>Italian marble</strong> and intelligent lighting. 
          Whether it's a compact modern bath in <strong>Noida</strong> or a royal washroom in <strong>Lucknow</strong>, 
          we create the perfect retreat.
        </p>
      </section>

      {/* 3. Design Gallery - SEO Optimized */}
      <section className="py-16 bg-gray-50 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-end mb-10">
            <div>
              <h3 className="text-3xl font-serif font-bold text-gray-900">Our Collections</h3>
              <p className="text-gray-500 mt-1">Explore latest bathroom trends in UP & Delhi</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {designs.map((design) => (
              <div key={design.id} className="group relative overflow-hidden shadow-lg cursor-pointer">
                {/* Image */}
                <div className="h-80 overflow-hidden">
                  <img 
                    src={design.image} 
                    alt={`${design.title} Luxury Bathroom Design`} // SEO Alt Text
                    loading="lazy"
                    className="w-full h-full object-cover transform group-hover:scale-105 transition duration-500"
                  />
                </div>
                {/* Text Overlay (Simple) */}
                <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/80 to-transparent p-6 pt-20">
                  <p className="text-amber-400 text-xs tracking-widest uppercase mb-1">{design.category}</p>
                  <h4 className="text-2xl font-serif text-white">{design.title}</h4>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Features Section */}
      <section className="py-16 px-6 bg-stone-900 text-white">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <div className="flex flex-col items-center">
            <Droplets className="w-10 h-10 text-amber-500 mb-4" />
            <h4 className="text-xl font-serif font-bold mb-2">Premium Fixtures</h4>
            <p className="text-gray-400 text-sm">We use top brands like <strong>Jaquar, Kohler & Grohe</strong> for durability.</p>
          </div>
          <div className="flex flex-col items-center">
            <Maximize className="w-10 h-10 text-amber-500 mb-4" />
            <h4 className="text-xl font-serif font-bold mb-2">Smart Layouts</h4>
            <p className="text-gray-400 text-sm">Space optimization (Wet & Dry Areas) for Indian homes.</p>
          </div>
          <div className="flex flex-col items-center">
            <Sparkles className="w-10 h-10 text-amber-500 mb-4" />
            <h4 className="text-xl font-serif font-bold mb-2">Luxury Finish</h4>
            <p className="text-gray-400 text-sm">Anti-skid tiles, Italian marble, and ambient cove lighting.</p>
          </div>
        </div>
      </section>

      {/* 5. CTA Section - Action Oriented */}
      <section className="py-20 px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-6">
          Plan Your Dream Bathroom
        </h2>
        <p className="text-gray-600 mb-8 max-w-xl mx-auto">
          Get expert advice on plumbing, layout, and tiles in Delhi, Lucknow & Varanasi.
        </p>
        <Link to="/contact" className="inline-block bg-amber-500 hover:bg-amber-600 text-white text-lg font-semibold px-8 py-3 rounded-sm transition shadow-lg uppercase tracking-widest">
          Book Consultation
        </Link>
      </section>
    </div>
  );
};

export default Bathroom;